// Runners directory — filter chips (AND), sortable columns, 20 per page.
// The whole view lives in the query string, so back from a runner returns
// to the same page.

import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RUNNER_FILTERS, RUNNER_SORTS, type AdminRunnerList, type AdminRunnerRow, type RunnerFilter, type RunnerSort } from "@paceroo/domain/views/admin";
import { IconSearch } from "../../components/Icons";
import { EmptyState, ErrorState, LoadingState, PageHeader, Pills, statusPills } from "../components/ui";
import { useAdminQuery } from "../lib/api";
import { fD, fRel, n0, parseWhen } from "../lib/format";
import { useShell } from "../shell/context";
import s from "./Runners.module.css";

const FILTER_LABEL: Record<RunnerFilter, string> = {
  verified: "Verified",
  unverified: "Unverified",
  active_plan: "Has active plan",
  assistant: "Assistant connected",
  signed_30d: "Signed up · 30d",
  seen_7d: "Seen · 7d",
  support_access: "Support access",
  banned: "Banned",
};

const PER_PAGE = { mobile: 15, tablet: 20, desktop: 20 } as const;

export function Runners() {
  const { bp, focusSearch } = useShell();
  const [params, setParams] = useSearchParams();
  const qParam = params.get("q") ?? "";
  const filters = (params.get("filter") ?? "").split(",").filter((f): f is RunnerFilter => (RUNNER_FILTERS as readonly string[]).includes(f));
  const sortParam = params.get("sort");
  const sort: RunnerSort = (RUNNER_SORTS as readonly string[]).includes(sortParam ?? "") ? (sortParam as RunnerSort) : "signed";
  const dir = params.get("dir") === "asc" ? "asc" : "desc";
  const page = Math.max(0, Number(params.get("page")) || 0);
  const per = PER_PAGE[bp];

  // The filter box types freely; the URL (and the query) follow 250 ms later.
  const [q, setQ] = useState(qParam);
  useEffect(() => setQ(qParam), [qParam]);
  useEffect(() => {
    if (q === qParam) return;
    const t = window.setTimeout(() => update({ q: q || null, page: null }), 250);
    return () => window.clearTimeout(t);
  });

  const update = (changes: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(changes)) {
          if (v === null || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );

  const query = new URLSearchParams({ sort, dir, page: String(page), per: String(per) });
  if (qParam) query.set("q", qParam);
  if (filters.length) query.set("filter", filters.join(","));
  const list = useAdminQuery<AdminRunnerList>(`runners?${query.toString()}`);

  if (list.error && !list.data) return <ErrorState page="Runners" error={list.error} onRetry={list.reload} onFind={focusSearch} />;
  if (!list.data) return <LoadingState page="runners" />;

  const d = list.data;
  const mobile = bp === "mobile";
  const wide = bp === "desktop";
  const anyFilter = filters.length > 0 || qParam !== "";
  const pages = Math.max(1, Math.ceil(d.total / d.perPage));
  const from = d.total ? d.page * d.perPage + 1 : 0;
  const to = Math.min(d.total, d.page * d.perPage + d.rows.length);
  const toggle = (f: RunnerFilter) => update({ filter: (filters.includes(f) ? filters.filter((x) => x !== f) : [...filters, f]).join(",") || null, page: null });
  const clear = () => {
    setQ("");
    update({ q: null, filter: null, page: null });
  };
  const sortBy = (k: RunnerSort) => update({ sort: k === "signed" ? null : k, dir: sort === k && dir === "desc" ? "asc" : null, page: null });

  const heads: Array<[RunnerSort | null, string, "left" | "right"]> = [
    ["name", "Runner", "left"],
    ["signed", "Signed up", "left"],
    ["seen", "Last seen", "left"],
    ["plans", "Plans", "right"],
    ...(wide
      ? ([
          ["exports", "Exports", "right"],
          ["assistants", "Assistants", "left"],
        ] as Array<[RunnerSort, string, "left" | "right"]>)
      : []),
    [null, "Status", "left"],
  ];
  const cols = wide ? "minmax(0,2.4fr) 80px 96px 56px 64px minmax(0,1fr) minmax(0,1.3fr)" : "minmax(0,2fr) 72px 90px 52px minmax(0,1.2fr)";

  return (
    <div className={s.page}>
      <PageHeader kicker="Runners · directory" accent={n0(d.all)} after=" runners." />

      <div className={s.controls}>
        <div className={s.filterRow}>
          <div className={clsx("input-shell", s.filter)}>
            <IconSearch size={15} />
            <input className={s.filterInput} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, email or id" aria-label="Filter runners" />
          </div>
          {mobile && (
            <select
              className={s.sortSelect}
              value={sort}
              aria-label="Sort runners"
              onChange={(e) => update({ sort: e.target.value === "signed" ? null : e.target.value, dir: e.target.value === "name" ? "asc" : null, page: null })}
            >
              <option value="signed">Newest sign-ups</option>
              <option value="seen">Last seen</option>
              <option value="exports">Most exports</option>
              <option value="name">Name</option>
            </select>
          )}
          <span className="t-mono-xs ink-mute">{n0(d.total)} shown</span>
        </div>
        <div className={clsx("no-scrollbar", s.chips, mobile ? s.chipsScroll : s.chipsWrap)}>
          {RUNNER_FILTERS.map((f) => (
            <button key={f} className={clsx("chip", s.chip)} data-on={filters.includes(f) ? "true" : undefined} onClick={() => toggle(f)} aria-pressed={filters.includes(f)}>
              {FILTER_LABEL[f]} <span className={clsx("t-mono-xs", s.chipCount)}>{n0(d.counts[f])}</span>
            </button>
          ))}
          {anyFilter && (
            <button className={clsx("btn-tertiary", s.clear)} onClick={clear}>
              Clear
            </button>
          )}
        </div>
      </div>

      {d.rows.length > 0 && !mobile && (
        <div className={s.table} role="table" aria-label="Runners">
          <div className={s.head} style={{ gridTemplateColumns: cols }} role="row">
            {heads.map(([k, label, align]) => (
              <button
                key={label}
                className={clsx(s.headBtn, sort === k && s.headOn)}
                style={{ textAlign: align }}
                disabled={!k}
                onClick={() => k && sortBy(k)}
                role="columnheader"
                aria-sort={sort === k ? (dir === "asc" ? "ascending" : "descending") : undefined}
              >
                {label}
                {sort === k ? (dir === "asc" ? " ↑" : " ↓") : ""}
              </button>
            ))}
          </div>
          {d.rows.map((r) => (
            <Link key={r.id} to={`/runners/${encodeURIComponent(r.id)}`} className={s.row} style={{ gridTemplateColumns: cols }} role="row">
              <span className={s.who}>
                <span className={clsx("t-label", s.name, s.ellipsis)}>{r.name}</span>
                <span className={clsx("t-mono-xs ink-mute", s.ellipsis)}>{r.email}</span>
              </span>
              <span className="t-mono-sm ink-soft">{fD(parseWhen(r.createdAt))}</span>
              <span className="t-mono-sm ink-soft">{r.lastSeenAt ? fRel(parseWhen(r.lastSeenAt)) : "—"}</span>
              <span className={clsx("t-mono-sm", s.right)}>{plansLabel(r)}</span>
              {wide && (
                <>
                  <span className={clsx("t-mono-sm", s.right)}>{r.exports === null ? "—" : n0(r.exports)}</span>
                  <span className={clsx("t-body-sm ink-soft", s.ellipsis)}>{r.assistants.length ? r.assistants.join(", ") : "—"}</span>
                </>
              )}
              <Pills pills={statusPills(r)} />
            </Link>
          ))}
        </div>
      )}

      {d.rows.length > 0 && mobile && (
        <div className={s.cards}>
          {d.rows.map((r) => (
            <Link key={r.id} to={`/runners/${encodeURIComponent(r.id)}`} className={s.card}>
              <span className={s.cardTop}>
                <span className={clsx("t-label", s.cardName)}>{r.name}</span>
                <Pills pills={statusPills(r)} />
              </span>
              <span className="t-mono-xs ink-mute">{r.email}</span>
              <span className="t-mono-xs ink-soft">
                Joined {fD(parseWhen(r.createdAt))} · seen {r.lastSeenAt ? fRel(parseWhen(r.lastSeenAt)) : "never"} · {plansLabel(r)} plan
                {r.exports !== null ? ` · ${n0(r.exports)} exports` : ""}
              </span>
            </Link>
          ))}
        </div>
      )}

      {d.rows.length === 0 && (
        <EmptyState
          title={d.all === 0 ? "No runners yet." : "No runners match."}
          body={
            d.all === 0
              ? "When the first person signs up they appear here, newest first. Nothing to do until then."
              : "Try fewer filters, or search the full email address."
          }
          {...(anyFilter
            ? {
                action: (
                  <button className={clsx("btn-secondary", s.clearBtn)} onClick={clear}>
                    Clear filters
                  </button>
                ),
              }
            : {})}
        />
      )}

      {pages > 1 && (
        <div className={s.pager}>
          <span className="t-mono-xs ink-mute">
            {n0(from)}–{n0(to)} of {n0(d.total)}
          </span>
          <div className={s.pagerBtns}>
            <button className={clsx("btn-secondary", s.pagerBtn)} disabled={d.page === 0} onClick={() => update({ page: d.page - 1 ? String(d.page - 1) : null })}>
              ← Newer
            </button>
            <button className={clsx("btn-secondary", s.pagerBtn)} disabled={d.page >= pages - 1} onClick={() => update({ page: String(d.page + 1) })}>
              Older →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function plansLabel(r: AdminRunnerRow): string {
  return `${r.plans}${r.archivedPlans ? ` +${r.archivedPlans}` : ""}`;
}
